import { Prediction, periods } from '@api';
import {
  HighLowTripleLineChart,
  HighLowTripleLineChartProps,
} from '@design/charts/high-low-triple-line-chart/HighLowTripleLineChart';
import { Icon } from '@design/components/icon/Icon';
import { HorizonSelector } from '@terminal/components/horizon-selector/HorizonSelector';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { PriceMovementLegend } from '../price-movement-legend/PriceMovementLegend';
import styles from './PriceMovementChart.module.scss';
import {
  clipPriceMovementToHorizon,
  getActiveHorizonIndex,
  getDefaultSelectedHorizon,
  getHorizonSelectionKey,
  getHorizonToIndexMap,
  getTranslatedHorizonToIndex,
} from './utils/price-movement-chart-utils';

export interface PriceMovementChartProps
  extends Pick<
    HighLowTripleLineChartProps,
    | 'bestHorizon'
    | 'yAxisLabel'
    | 'isBordered'
    | 'maxHeight'
    | 'isFixedIncome'
    | 'useDynamicDaysLabel'
    | 'hiddenHighlightedHorizonLabels'
    | 'useSimpleRightAxis'
  > {
  prediction: Prediction[];
  horizonToIndexOffset?: number;
  defaultHorizonToIndex?: Map<string, number>;
  defaultHorizons?: string[];
  legendLabel?: string;
  isMedian?: boolean;
  snakeTs?: { index: number; value: number }[];
  isShowCumulativeReturn?: boolean;
  showHorizonSelector?: boolean;
  horizonSelectionResetKey?: string;
}

export const PriceMovementChart = ({
  prediction,
  bestHorizon,
  horizonToIndexOffset = 0,
  defaultHorizonToIndex,
  defaultHorizons,
  legendLabel,
  isMedian = true,
  snakeTs = [],
  isShowCumulativeReturn = true,
  showHorizonSelector = false,
  horizonSelectionResetKey,
  ...rest
}: PriceMovementChartProps) => {
  const { t } = useTranslation(['scenario', 'screener', 'widget']);
  const rawHorizonToIndex =
    defaultHorizonToIndex ?? getHorizonToIndexMap(horizonToIndexOffset);
  const horizonKeys = defaultHorizons ?? periods;
  const selectionKey = getHorizonSelectionKey(
    horizonKeys,
    bestHorizon,
    horizonSelectionResetKey
  );
  const defaultHorizon = getDefaultSelectedHorizon(horizonKeys, bestHorizon);
  const [selectedHorizon, setSelectedHorizon] = useState(defaultHorizon);
  const [appliedSelectionKey, setAppliedSelectionKey] = useState(selectionKey);
  const hasSelectionSourceChanged = appliedSelectionKey !== selectionKey;
  const activeHorizon = hasSelectionSourceChanged
    ? defaultHorizon
    : selectedHorizon;

  if (hasSelectionSourceChanged) {
    setAppliedSelectionKey(selectionKey);
    setSelectedHorizon(defaultHorizon);
  }

  const handleHorizonClick = (index: number) => {
    const horizon = horizonKeys[index];
    if (horizon) {
      setSelectedHorizon(horizon);
    }
  };

  const visible = showHorizonSelector
    ? clipPriceMovementToHorizon({
        prediction,
        snakeTs,
        horizons: horizonKeys,
        selectedHorizon: activeHorizon,
        horizonToIndex: rawHorizonToIndex,
      })
    : { prediction, snakeTs, horizons: horizonKeys };

  const tooltipLabels = {
    /* i18next-extract-mark-context-next-line  ["p80"] */
    high: t('scenario:drawer.summaryTable.row', {
      context: 'p80',
    }),
    /* i18next-extract-mark-context-next-line  ["median", "mean"] */
    median: t('scenario:drawer.summaryTable.row', {
      context: isMedian ? 'median' : 'mean',
    }),
    /* i18next-extract-mark-context-next-line  ["p20"] */
    low: t('scenario:drawer.summaryTable.row', {
      context: 'p20',
    }),
  };
  const daysLabel = t('screener:screenerEvents.days', {
    count: visible.prediction.length,
  });
  const translatedHorizons = visible.horizons.map(period =>
    t('widget:priceMovement.horizon', {
      context: period,
    })
  );
  const horizonToIndex = getTranslatedHorizonToIndex(
    visible.horizons,
    rawHorizonToIndex,
    period =>
      t('widget:priceMovement.horizon', {
        context: period,
      })
  );
  const translatedBestHorizon = bestHorizon
    ? t('widget:priceMovement.horizon', { context: bestHorizon })
    : undefined;

  return (
    <div
      className={styles.priceMovementChartRoot}
      data-testid="price-movement-chart"
      data-price-movement-chart
      data-selected-horizon={showHorizonSelector ? activeHorizon : undefined}
      data-series-length={visible.prediction.length}
    >
      {showHorizonSelector && horizonKeys.length > 0 && (
        <HorizonSelector
          className={styles.horizonSelector}
          testId="price-movement-horizon-selector"
          horizonLabels={horizonKeys.map(horizon => (
            <span className={styles.horizonLabel} key={horizon}>
              {horizon === bestHorizon && (
                <Icon
                  size={16}
                  iconName="StarFill"
                  fillColor="var(--icon-primary)"
                  className={styles.starIcon}
                />
              )}
              {t('widget:priceMovement.horizon', { context: horizon })}
            </span>
          ))}
          onClick={handleHorizonClick}
          activeHorizonIndex={getActiveHorizonIndex(horizonKeys, activeHorizon)}
        />
      )}
      <PriceMovementLegend
        bestHorizon={bestHorizon}
        legendLabel={legendLabel}
        isShowCumulativeReturn={isShowCumulativeReturn}
      />
      <HighLowTripleLineChart
        ts={visible.prediction}
        snakeTs={visible.snakeTs}
        bestHorizon={translatedBestHorizon}
        horizonToIndex={horizonToIndex}
        highlightedHorizons={translatedHorizons}
        tooltipLabels={tooltipLabels}
        daysLabel={daysLabel}
        {...rest}
      />
    </div>
  );
};
